<template>
    <jet-form-section>
        <template #title>{{ $t("profile.team-link.title") }}</template>

        <template #description>{{
            $t("profile.team-link.description")
        }}</template>

        <template #form>
            <div class="sm:col-span-4 col-span-6">
                <div>
                    <div class="mb-1 text-sm flex justify-between">
                        <p class="font-medium text-iron-700">
                            {{ $t("event_types.booking_page.link") }}
                        </p>
                    </div>

                    <InputWithPrefix
                        :model-value="teamUrlName"
                        :variant="variant"
                        @update:model-value="updateValue"
                    >
                        <template #prefix>
                            <span
                                class="h-10 rounded-l-md border-iron-300 bg-iron-50 px-3 text-iron-600 sm:text-sm inline-flex shrink-0 items-center border border-r-0"
                                >{{ `zapti.me/` }}</span
                            >
                        </template>
                    </InputWithPrefix>

                    <div v-if="error" class="mt-2 text-sm text-error-500">
                        {{ error.reason }}
                    </div>
                </div>
            </div>
        </template>

        <template #actions>
            <Button
                :disabled="!isValid"
                :loading="loading"
                :recently-successful="recentlySaved"
                size="sm"
                @click="save"
            >
                {{ $t("save_changes") }}
            </Button>
        </template>
    </jet-form-section>
</template>

<script lang="ts" setup>
import JetFormSection from "../../Jetstream/FormSection.vue";
import InputWithPrefix from "@/components/ui/input-with-prefix/InputWithPrefix.vue";
import { computed, ref } from "vue";
import { Button } from "@/components/ui/button";
import axios from "axios";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";

import { refAutoReset } from "@vueuse/core";

import { useI18n } from "vue-i18n";

import { usePage } from "@inertiajs/vue3";

const dirty = ref(false);
const loading = ref(false);

const recentlySaved = refAutoReset(false, 2000);

const error = ref<{ reason: string }>({
    reason: "",
});

const page = usePage();

const { t } = useI18n();

const teamUrlName = ref(page.props.personalTeam.slug);

const variant = computed(() => {
    return error.value.reason ? "error" : "default";
});

const isValid = computed(() => {
    return !!error.value.reason === false;
});

const alphanumericDashPattern = /^[a-zA-Z0-9-]+$/;

function validateSlug(newSlug: string) {
    newSlug = newSlug.replaceAll(" ", "-");

    if (newSlug.length > 64) {
        error.value.reason = t("event_types.booking_page.link_length");
    } else if (!alphanumericDashPattern.test(newSlug)) {
        error.value.reason = t("event_types.booking_page.link_special_chars");
    } else {
        error.value = { reason: "" };
    }

    return newSlug;
}

const updateValue = (value: string) => {
    teamUrlName.value = validateSlug(value);
    dirty.value = true;
};

async function save() {
    if (error.value.reason) {
        return;
    }

    loading.value = true;

    try {
        const res = await axios.post(
            route("app.teams.slugs.check", {
                team: page.props.personalTeam.id,
            }),
            {
                slug: teamUrlName.value,
            },
        );

        if (res.data.data.slug === teamUrlName.value) {
            router.put(
                route("app.teams.slugs.update", {
                    team: page.props.personalTeam.id,
                }),
                {
                    slug: teamUrlName.value,
                },
                {
                    preserveScroll: true,
                    preserveState: true,
                    onFinish() {
                        recentlySaved.value = true;
                        loading.value = false;
                    },
                },
            );
        } else {
            loading.value = false;
            error.value.reason = t(
                "event_types.booking_page.link_alerady_exists",
            );
        }
        dirty.value = false;
    } catch (e) {
        // @ts-expect-error
        error.value.reason = e.response.data.message;
    }
}
</script>
